import { EngagementChannelsConfig } from '@/types/configuration'
import { Card, Checkbox, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useEffect, useState } from 'react'
import { ConfigFormHeader } from '@/components/ConfigFormHeader'

/**
 * Format phone number to (XXX) XXX-XXXX for display, consistent with BusinessBasicsForm
 */
const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, '')
  const limitedDigits = digits.slice(0, 10)
  if (limitedDigits.length === 0) return ''
  if (limitedDigits.length <= 3) return `(${limitedDigits}`
  if (limitedDigits.length <= 6) return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`
  return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`
}

/** Convert E.164 (+15551234567) to display format for the input */
const e164ToDisplay = (value: string): string => {
  const digits = value.replace(/\D/g, '')
  const ten = digits.length >= 10 ? digits.slice(-10) : digits
  return formatPhoneNumber(ten)
}

/** Convert display format to E.164 for the API */
const displayToE164 = (value: string): string => {
  const digits = value.replace(/\D/g, '')
  if (digits.length !== 10) return value
  return `+1${digits}`
}

/** Validate optional US phone: if any digits entered, must be exactly 10 digits */
function validateSmsPhoneNumber(value: string | undefined): string | null {
  if (!value || !value.trim()) return null
  const digits = value.replace(/\D/g, '')
  if (digits.length !== 10) return 'Enter a valid 10-digit US phone number'
  return null
}

/** Optional, but anything entered has to be a positive whole number — the API rejects the rest. */
function validateAgentEmployeeId(value: number | undefined): string | null {
  if (value == null || (value as unknown) === '') return null
  if (!Number.isInteger(value) || value <= 0) return 'Enter a valid ProMax employee ID'
  return null
}

interface EngagementChannelsFormProps {
  data: EngagementChannelsConfig | null
  onSave: (data: EngagementChannelsConfig) => void
  isSaving: boolean
}

export function EngagementChannelsForm({ data, onSave, isSaving }: EngagementChannelsFormProps) {
  const form = useForm<EngagementChannelsConfig>({
    initialValues: {
      sms: { enabled: true, smsPhoneNumber: '' },
      email: { enabled: true },
      preferredChannel: 'SMS',
      staffContactDisengagement: 'all_channels',
      expirationPeriod: 168, // hours - default 7 days
    },
    validate: {
      sms: {
        smsPhoneNumber: validateSmsPhoneNumber,
      },
      agentEmployeeId: validateAgentEmployeeId,
    },
  })

  // Local state for expiration period input to allow free typing
  const [expirationPeriodInput, setExpirationPeriodInput] = useState<string>('168')

  useEffect(() => {
    if (data) {
      const raw = data as typeof data & { defaultSmsNumber?: string }
      const configWithoutDefault = Object.fromEntries(
        Object.entries(raw).filter(([key]) => key !== 'defaultSmsNumber'),
      ) as EngagementChannelsConfig
      const displaySmsPhone = configWithoutDefault.sms?.smsPhoneNumber
        ? e164ToDisplay(configWithoutDefault.sms.smsPhoneNumber)
        : ''
      form.setValues({
        ...configWithoutDefault,
        staffContactDisengagement: configWithoutDefault.staffContactDisengagement ?? 'all_channels',
        sms: {
          ...configWithoutDefault.sms,
          enabled: configWithoutDefault.sms?.enabled ?? true,
          smsPhoneNumber: displaySmsPhone,
        },
      })
      setExpirationPeriodInput((configWithoutDefault.expirationPeriod ?? 168).toString())
    }
  }, [data])

  const handleSubmit = (values: EngagementChannelsConfig) => {
    const payload: EngagementChannelsConfig = {
      ...values,
      sms: {
        ...values.sms,
        smsPhoneNumber: values.sms?.smsPhoneNumber?.trim()
          ? displayToE164(values.sms.smsPhoneNumber)
          : undefined,
      },
      // Clearing the field yields '' rather than undefined, which the API reads as a bad number
      // instead of "not set".
      agentEmployeeId: Number.isInteger(values.agentEmployeeId) ? values.agentEmployeeId : undefined,
    }
    onSave(payload)
  }

  const smsPhoneDescription = "The dealership's SMS number. Inbound replies are matched to the dealer by it."

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <ConfigFormHeader onReset={() => form.reset()} isSaving={isSaving} />
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text fw={600}>SMS Settings</Text>
            <Stack gap="md">
              <Checkbox label="Enable SMS" {...form.getInputProps('sms.enabled', { type: 'checkbox' })} />
              <TextInput
                label="SMS Phone Number"
                description={smsPhoneDescription}
                placeholder="(555) 123-4567"
                maxLength={14}
                value={form.values.sms?.smsPhoneNumber ?? ''}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value)
                  form.setFieldValue('sms.smsPhoneNumber', formatted)
                }}
                onBlur={() => form.validateField('sms.smsPhoneNumber')}
                error={form.getInputProps('sms.smsPhoneNumber').error}
              />
            </Stack>
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text fw={600}>Email Settings</Text>
            <Checkbox label="Enable Email" {...form.getInputProps('email.enabled', { type: 'checkbox' })} />
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text fw={600}>Agent Identity</Text>
            <NumberInput
              label="ProMax Employee ID"
              description="The dealership employee BDC AI acts on behalf of when sending."
              placeholder="12345"
              min={1}
              step={1}
              allowDecimal={false}
              allowNegative={false}
              allowLeadingZeros={false}
              thousandSeparator={false}
              hideControls
              {...form.getInputProps('agentEmployeeId')}
            />
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Select
              label="Preferred Channel"
              description="Primary channel for customer communications"
              data={[
                { value: 'SMS', label: 'SMS' },
                { value: 'EMAIL', label: 'Email' },
              ]}
              {...form.getInputProps('preferredChannel')}
            />
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Select
              label="When dealer staff contact a customer"
              description="What BDC AI does after a staff member texts or calls a customer. Reminders and follow-ups keep running either way."
              data={[
                { value: 'all_channels', label: 'Stop engaging on every channel' },
                { value: 'same_channel', label: 'Stop only on the channel staff used' },
                { value: 'off', label: 'Keep engaging' },
              ]}
              allowDeselect={false}
              {...form.getInputProps('staffContactDisengagement')}
            />
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text fw={600}>Conversation Expiration</Text>
            <TextInput
              label="Expiration Period (hours)"
              description="How long before a conversation is considered expired (default: 168 hours = 7 days)"
              type="number"
              min={1}
              max={8760} // 1 year in hours
              placeholder="168"
              value={expirationPeriodInput}
              onChange={e => {
                const value = e.currentTarget.value
                // Update local state immediately to allow free typing
                setExpirationPeriodInput(value)
              }}
              onBlur={e => {
                const value = e.currentTarget.value
                // Validate and set default on blur if empty or invalid
                if (value === '' || isNaN(Number(value)) || Number(value) < 1) {
                  const defaultValue = 168
                  form.setFieldValue('expirationPeriod', defaultValue)
                  setExpirationPeriodInput(defaultValue.toString())
                } else {
                  const numValue = parseInt(value, 10)
                  if (numValue > 8760) {
                    form.setFieldValue('expirationPeriod', 8760)
                    setExpirationPeriodInput('8760')
                  } else {
                    form.setFieldValue('expirationPeriod', numValue)
                    setExpirationPeriodInput(numValue.toString())
                  }
                }
              }}
              error={form.errors.expirationPeriod}
            />
            <Text size="xs" c="dimmed">
              Common values: 24 hours (1 day), 168 hours (7 days), 720 hours (30 days)
            </Text>
          </Stack>
        </Card>

      </Stack>
    </form>
  )
}
